import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineRounded";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Stack, Tab } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import ActionsMenuButton from "@entrilia/shared/components/ActionsMenuButton";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { Category } from "../../../../api/types/accessTypes";
import type { ContactDetails } from "../../../../api/types/fundStructureTypes";
import type { ObjectClassDefinition, ObjectPermissions } from "../../../../api/types/objectTypes";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import ObjectTitle from "../../../common/ObjectTitle";
import FullHeightFlexPage from "../../../common/layout/FullHeightFlexPage";
import StretchedScrollableContent from "../../../common/layout/StretchedScrollableContent";
import { getDataCatalogueEntityType } from "../../../entityFields/entityTypesHelper";
import EntityCardFiles from "../../../entityFields/files/EntityCardFiles";
import { LockedFundraisingPopover } from "../common/LockedPortalPagePopovers";
import ContactPortalHeaderActions from "./ContactPortalHeaderActions";
import DeleteContactDialog from "./DeleteContactDialog";
import ContactCommunicationMatrixTab from "./communication-matrix/ContactCommunicationMatrixTab";
import ContactDocumentActivityTab from "./document-activity/ContactDocumentActivityTab";
import ContactDetailsDocumentsTab from "./documents/ContactDetailsDocumentsTab";
import ContactDetailsEmailsTab from "./emails/ContactDetailsEmailsTab";
import ContactFundraisingDocumentsTab from "./fundraising-documents/ContactFundraisingDocumentsTab";
import ContactDetailsMainTab from "./mainInfo/ContactDetailsMainTab";
import { PortalAccessContextProvider } from "./mainInfo/portal-access/PortalAccessContext";

interface Props {
  categories: Category[];
  contactDetails: ContactDetails;
  objectDefinition: ObjectClassDefinition;
  objectPermissions: ObjectPermissions;
  onContactDetailsChange: (details: Partial<ContactDetails>) => void;
  onUpdateContactDetails: () => void;
}

const tabPanelSx = { height: "100%", m: 0, px: 3, pt: 2.5, pb: 0 };

const ContactDetailsPage = ({
  categories,
  contactDetails,
  objectDefinition,
  objectPermissions,
  onContactDetailsChange,
  onUpdateContactDetails,
}: Props) => {
  const { tab } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAnyPermission, isPortalFeatureEnabled, investorPortalApp } = useClientContext();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const hasPermissionsToManageContacts = hasAnyPermission(objectPermissions.objectDataWritePermissions);
  const hasPermissionsToViewFiles = hasAnyPermission(objectPermissions.filesReadPermissions);
  const hasPermissionsToManageFiles = hasAnyPermission(objectPermissions.filesWritePermissions);
  const hasPermissionsToViewFundraisings = hasAnyPermission(["ViewFundraisings", "ManageFundraisings"]);
  const hasAccessToDocumentActivity =
    hasAnyPermission(["ViewInvestorPortalDocumentViewsAnalytics"]) && Boolean(investorPortalApp);

  const selectedTab = tab || "main";

  const navigateToTab = (newTab: string, options?: { push?: boolean }) => {
    navigate(`../${newTab}`, { relative: "path", replace: !options?.push, state: location.state });
  };

  const handleManageAccess = () => navigateToTab("communication-matrix", { push: true });
  const handleViewEmails = () => navigateToTab("emails", { push: true });
  const handleViewDocumentActivity = () => navigateToTab("document-activity", { push: true });

  const handleContactDeleted = () => {
    setIsDeleteDialogOpen(false);
    navigate("..");
  };

  const { contact } = contactDetails;
  const requestedAddressRef = useRef(contact.email);
  const [loadedAddress, setLoadedAddress] = useState(contact.email);

  const getPortalAccess = useCallback(
    (signal?: AbortSignal) => {
      requestedAddressRef.current = contact.email;
      return adminApi.getContactPortalAccess(contact.id, signal);
    },
    [contact.id, contact.email]
  );

  const handlePortalAccessLoaded = useCallback(() => setLoadedAddress(requestedAddressRef.current), []);

  const [loadedPortalAccess, portalAccessError, { isFetching, fetch: refetchPortalAccess }] = useFetch(
    getPortalAccess,
    {
      disabled: !investorPortalApp,
      onSetData: handlePortalAccessLoaded,
    }
  );

  const portalAccess = loadedAddress === contact.email ? loadedPortalAccess : undefined;

  if (portalAccessError) {
    logError(portalAccessError, "[ContactDetailsPage] getContactPortalAccess");
  }

  const isPortalAccessLoading = portalAccess === undefined && (isFetching || loadedAddress !== contact.email);

  return (
    <PortalAccessContextProvider
      contact={contact}
      portalAccess={portalAccess}
      error={portalAccessError}
      isLoading={isPortalAccessLoading}
      onRefetch={refetchPortalAccess}
    >
      <TabContext value={selectedTab}>
        <FullHeightFlexPage>
          <GeneralPageHeader
            TitleComponent={
              <ObjectTitle title={contact.name} secondaryTitle={contact.email} typeName={objectDefinition.objectName} />
            }
            hideBorderLine
            showDefaultBackButtonTo=".."
          >
            <ContactPortalHeaderActions
              contact={contact}
              hasPermissionsToManageContacts={hasPermissionsToManageContacts}
              onInvitationSent={onUpdateContactDetails}
            />
            <ActionsMenuButton
              items={[
                {
                  icon: <DeleteOutlineIcon color="error" />,
                  label: "Delete",
                  onClick: () => setIsDeleteDialogOpen(true),
                  disabled: !hasPermissionsToManageContacts,
                  tooltip: hasPermissionsToManageContacts
                    ? undefined
                    : "You do not have permission to delete contacts.",
                },
              ]}
            />
          </GeneralPageHeader>
          <TabList onChange={(_, tab) => navigateToTab(tab)}>
            <Tab sx={{ ml: 3 }} label="Main Info" value="main" />
            <Tab label="Investor Documents" value="documents" />
            {hasPermissionsToViewFundraisings && (
              <Tab
                disabled={!investorPortalApp}
                component={investorPortalApp ? "button" : "div"}
                label={
                  investorPortalApp ? (
                    "Fundraising Documents"
                  ) : (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ alignItems: "center", pointerEvents: "auto" }}
                    >
                      Fundraising Documents
                      <LockedFundraisingPopover />
                    </Stack>
                  )
                }
                value="fundraising-documents"
              />
            )}
            {hasAccessToDocumentActivity && <Tab label="Document Activity" value="document-activity" />}
            {hasPermissionsToViewFiles && isPortalFeatureEnabled("EntityFilesEnabled") && (
              <Tab label="Files" value="files" />
            )}
            <Tab label="Emails" value="emails" disabled={!contact.email} />
            <Tab label="Communication Matrix" value="communication-matrix" />
          </TabList>
          <StretchedScrollableContent>
            <TabPanel value="main" sx={{ ...tabPanelSx, backgroundColor: "background.grey" }}>
              <ContactDetailsMainTab
                contactDetails={contactDetails}
                objectDefinition={objectDefinition}
                objectPermissions={objectPermissions}
                onContactDetailsChange={onContactDetailsChange}
                onUpdateContactDetails={onUpdateContactDetails}
                onManageAccess={handleManageAccess}
                onViewEmails={handleViewEmails}
                onViewDocumentActivity={handleViewDocumentActivity}
              />
            </TabPanel>
            <TabPanel value="documents" sx={tabPanelSx}>
              <ContactDetailsDocumentsTab
                contactId={contact.id}
                categories={categories}
                contactPermissions={contactDetails.permissions}
              />
            </TabPanel>
            {hasPermissionsToViewFundraisings && investorPortalApp && (
              <TabPanel value="fundraising-documents" sx={tabPanelSx}>
                <ContactFundraisingDocumentsTab
                  contactId={contact.id}
                  categories={categories}
                  contactPermissions={contactDetails.permissions}
                />
              </TabPanel>
            )}
            {hasAccessToDocumentActivity && (
              <TabPanel value="document-activity" sx={tabPanelSx}>
                <ContactDocumentActivityTab contactId={contact.id} />
              </TabPanel>
            )}
            {hasPermissionsToViewFiles && isPortalFeatureEnabled("EntityFilesEnabled") && (
              <TabPanel value="files" sx={{ height: "100%", m: 0, px: 0, py: 0 }}>
                <EntityCardFiles
                  entityType={getDataCatalogueEntityType(objectDefinition.objectType)}
                  entityId={contact.id}
                  entityName={contact.name}
                  hasPermissionsToManage={hasPermissionsToManageFiles}
                />
              </TabPanel>
            )}
            <TabPanel value="emails" sx={tabPanelSx}>
              <ContactDetailsEmailsTab contactId={contact.id} />
            </TabPanel>
            <TabPanel value="communication-matrix" sx={tabPanelSx}>
              <ContactCommunicationMatrixTab
                contactId={contact.id}
                categories={categories}
                onUpdateContactDetails={onUpdateContactDetails}
              />
            </TabPanel>
          </StretchedScrollableContent>
        </FullHeightFlexPage>
      </TabContext>
      <DeleteContactDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDeleted={handleContactDeleted}
        contact={contact}
      />
    </PortalAccessContextProvider>
  );
};

export default ContactDetailsPage;
